import { Link, Route, Routes, useLocation } from 'react-router-dom';

import { AppShell } from './components/layout/AppShell';
import { Icon, type IconName } from './components/ui';
import { RequireAuth } from './lib/auth';
import { CompanyProvider, useCompany } from './lib/companyContext';
import { PeriodProvider } from './lib/periodContext';
import { FunilCarrinho } from './features/funis/pages/FunilCarrinho';
import { FunilExpirados } from './features/funis/pages/FunilExpirados';
import { VisaoGeral } from './features/visao-geral/pages/VisaoGeral';
import { LeadDetalhe } from './features/leads/pages/LeadDetalhe';
import { Configuracoes } from './features/configuracoes/pages/Configuracoes';
import { Comercial } from './features/comercial/pages/Comercial';
import { AdminCentral } from './features/admin-central/pages/AdminCentral';
import { AdminEmpresas } from './features/admin-empresas/pages/AdminEmpresas';
import { AdminEmpresaNova } from './features/admin-empresas/pages/AdminEmpresaNova';
import { AdminEmpresaDetalhe } from './features/admin-empresas/pages/AdminEmpresaDetalhe';
import { AdminComissoes } from './features/admin-comissoes/pages/AdminComissoes';
import { Disparos } from './features/admin-disparos/pages/Disparos';
import { DisparosWhatsApp } from './features/admin-disparos/pages/DisparosWhatsApp';
import { DisparosEmail } from './features/admin-disparos/pages/DisparosEmail';
import { Modelos } from './features/admin-modelos/pages/Modelos';
import { Campanhas } from './pages/Campanhas';
import { Leads } from './pages/Leads';
import { Login } from './pages/Login';

/**
 * Mapa de navegacao do painel (RNF15 / RNF12).
 *
 * A lista de rotas mora junto das rotas, e nao dentro da Sidebar, para que
 * exista uma unica verdade: acrescentar uma tela aqui ja a faz aparecer no menu
 * do desktop e no drawer do mobile, sem editar tres arquivos.
 *
 * DOIS MUNDOS, UM MENU (decisao do dono, 24/09/2026):
 *  - grupo `empresa`: o CRM que CADA lojista ve -- visao geral com os funis,
 *    funil de carrinho abandonado, funil de expirados, leads, contatos e
 *    configuracoes. Sempre escopado a empresa em foco (`useCompany`).
 *  - grupo `plataforma`: so para a equipe do Converte Facil (`platform_admin`):
 *    a Central com todas as empresas e os totais gerais, o cadastro de
 *    empresas, as comissoes e as ferramentas de disparo e modelos. Disparos e
 *    modelos sao OPERADOS POR NOS em nome das empresas, por isso nao ficam no
 *    menu do lojista.
 *
 * `/login` e a unica rota publica. Todo o resto fica atras de `RequireAuth`, e
 * o `PeriodProvider` so existe na area autenticada -- o filtro de periodo nao
 * faz sentido sem empresa.
 */

export type NavGroup = 'empresa' | 'plataforma';

export interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly hint: string;
  readonly icon: IconName;
  readonly end: boolean;
  readonly group: NavGroup;
  /** Visivel so para `platform_admin`. */
  readonly adminOnly: boolean;
}

export const NAV_GROUP_LABEL: Readonly<Record<NavGroup, string>> = {
  empresa: 'Empresa',
  plataforma: 'Converte Fácil · administração',
};

export const NAV_ITEMS: readonly NavItem[] = [
  { to: '/', label: 'Visão geral', hint: 'Funis, retorno e tendência', icon: 'painel', end: true, group: 'empresa', adminOnly: false },
  { to: '/funis/carrinho', label: 'Carrinho abandonado', hint: 'Régua, toques e ofertas', icon: 'carrinho', end: false, group: 'empresa', adminOnly: false },
  { to: '/funis/expirados', label: 'Expirados', hint: 'Assinaturas vencidas', icon: 'calendario', end: false, group: 'empresa', adminOnly: false },
  { to: '/leads', label: 'Leads', hint: 'Quem abandonou e o histórico', icon: 'leads', end: false, group: 'empresa', adminOnly: false },
  { to: '/campanhas', label: 'Contatos', hint: 'Ligações e mensagens', icon: 'telefone', end: false, group: 'empresa', adminOnly: false },
  { to: '/comercial', label: 'Comercial', hint: 'Respostas da régua e vendedoras', icon: 'usuarios', end: false, group: 'empresa', adminOnly: false },
  { to: '/configuracoes', label: 'Configurações', hint: 'Marca, régua e integrações', icon: 'config', end: false, group: 'empresa', adminOnly: false },

  { to: '/admin', label: 'Central', hint: 'Todas as empresas e totais', icon: 'grafico', end: true, group: 'plataforma', adminOnly: true },
  { to: '/admin/empresas', label: 'Empresas', hint: 'Cadastro e CRM de cada uma', icon: 'empresa', end: false, group: 'plataforma', adminOnly: true },
  { to: '/admin/comissoes', label: 'Comissões', hint: 'Faturamento do Converte Fácil', icon: 'comissao', end: false, group: 'plataforma', adminOnly: true },
  { to: '/admin/disparos', label: 'Disparos', hint: 'WhatsApp e e-mail em massa', icon: 'disparo', end: false, group: 'plataforma', adminOnly: true },
  { to: '/admin/modelos', label: 'Modelos', hint: 'Templates de WhatsApp, e-mail e voz', icon: 'modelo', end: false, group: 'plataforma', adminOnly: true },
];

function NaoEncontradaPage(): JSX.Element {
  return (
    <section className="flex flex-col items-start gap-4">
      <h1 className="text-2xl font-semibold tracking-tight text-ink">Página não encontrada</h1>
      <p className="max-w-prose text-sm text-ink-muted">O endereço acessado não existe neste painel.</p>
      <Link to="/" className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand/90">
        Voltar à visão geral
      </Link>
    </section>
  );
}

/**
 * Tela em construcao: aparece na previa para as rotas ja previstas no menu
 * cujas paginas ainda nao foram integradas. Diz o que vai existir ali, para
 * quem navega a previa saber que o caminho e intencional.
 */
function EmConstrucaoPage(): JSX.Element {
  const { pathname } = useLocation();
  const item = NAV_ITEMS.find((i) => (i.end ? i.to === pathname : pathname.startsWith(i.to)));
  return (
    <section className="flex flex-col items-start gap-4 rounded-2xl border border-dashed border-edge bg-surface px-6 py-12">
      <span aria-hidden="true" className="grid h-12 w-12 place-items-center rounded-xl bg-brand/10 text-brand">
        <Icon name={item?.icon ?? 'painel'} size="lg" />
      </span>
      <h1 id="titulo-da-pagina" className="text-2xl font-semibold tracking-tight text-ink">
        {item?.label ?? 'Em construção'}
      </h1>
      <p className="max-w-prose text-sm text-ink-muted">
        {item === undefined ? 'Esta tela ainda está sendo construída.' : `${item.hint}. Esta tela está sendo construída e entra na prévia em seguida.`}
      </p>
      <Link to="/" className="rounded-lg border border-line bg-surface px-4 py-2 text-sm font-medium text-ink transition-colors hover:border-brand/60 hover:text-brand">
        Voltar à visão geral
      </Link>
    </section>
  );
}

/** Rotas do grupo `plataforma` exigem `platform_admin`; o lojista cai na visao geral. */
function SomenteAdmin({ children }: { readonly children: JSX.Element }): JSX.Element {
  const { isPlatformAdmin } = useCompany();
  if (!isPlatformAdmin) {
    return <NaoEncontradaPage />;
  }
  return children;
}

function AreaAutenticada(): JSX.Element {
  return (
    <RequireAuth>
      <PeriodProvider>
        <CompanyProvider>
          <AppShell>
            <Routes>
              <Route path="/" element={<VisaoGeral />} />
              <Route path="/funis/carrinho" element={<FunilCarrinho />} />
              <Route path="/funis/expirados" element={<FunilExpirados />} />
              <Route path="/leads" element={<Leads />} />
              <Route path="/leads/:leadId" element={<LeadDetalhe />} />
              <Route path="/campanhas" element={<Campanhas />} />
              <Route path="/comercial" element={<Comercial />} />
              <Route path="/configuracoes" element={<Configuracoes />} />
              <Route path="/admin" element={<SomenteAdmin><AdminCentral /></SomenteAdmin>} />
              <Route path="/admin/empresas" element={<SomenteAdmin><AdminEmpresas /></SomenteAdmin>} />
              <Route path="/admin/empresas/nova" element={<SomenteAdmin><AdminEmpresaNova /></SomenteAdmin>} />
              <Route path="/admin/empresas/:companyId" element={<SomenteAdmin><AdminEmpresaDetalhe /></SomenteAdmin>} />
              <Route path="/admin/comissoes" element={<SomenteAdmin><AdminComissoes /></SomenteAdmin>} />
              <Route path="/admin/disparos" element={<SomenteAdmin><Disparos /></SomenteAdmin>} />
              <Route path="/admin/disparos/whatsapp" element={<SomenteAdmin><DisparosWhatsApp /></SomenteAdmin>} />
              <Route path="/admin/disparos/email" element={<SomenteAdmin><DisparosEmail /></SomenteAdmin>} />
              <Route path="/admin/modelos" element={<SomenteAdmin><Modelos /></SomenteAdmin>} />
              <Route path="/admin/*" element={<SomenteAdmin><EmConstrucaoPage /></SomenteAdmin>} />
              <Route path="*" element={<NaoEncontradaPage />} />
            </Routes>
          </AppShell>
        </CompanyProvider>
      </PeriodProvider>
    </RequireAuth>
  );
}

export function AppRoutes(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/*" element={<AreaAutenticada />} />
    </Routes>
  );
}
